import AuditorsManager from '../AuditorsManager';
import PortalsManager from '../PortalsManager';
import ChecklistTemplateManager from '../ChecklistTemplateManager';
import ChatSettingsManager from '../ChatSettingsManager';
import AttendanceSettingsManager from '../AttendanceSettingsManager';
import FinanceSettingsCard from '../finance/FinanceSettingsCard';
import SecurityPanel from '../../../components/SecurityPanel';
import { OrganizationPanel, OrgContactPanel, SubscriptionPanel } from './OrgAboutPanels';
import { canAccessNavKey } from '../navItems';
import {
  AuditorIcon, ChecklistIcon, PortalIcon, ChatIcon, AttendanceIcon,
  InvoiceIcon, SecurityIcon, OrganizationIcon, ContactIcon, SubscriptionIcon,
} from './SettingsIcons';

// Every settings screen, in one list.
//
// THE INDEX AND THE DETAIL VIEW BOTH READ THIS. That is the point: a settings
// app whose menu and whose router are maintained separately grows rows that
// lead nowhere and screens nobody can reach. Adding a setting means adding one
// entry here and nothing else.
//
// `slug` is in the URL, so it is permanent — renaming a `label` is free, but
// renaming a slug breaks anyone's bookmark.
//
// Grouping follows OWNERSHIP, which is what actually predicts where someone
// looks for a setting:
//   • Practice     — the firm's own reference data and standard procedure
//   • Clients      — anything a client experiences
//   • People       — how the firm's own staff are managed
//   • Billing      — money going out to clients
//   • Security     — the signed-in person's own account
//   • About        — the organization record itself, owned by the platform
// About is last for the same reason it is last on a phone: it is the screen you
// read, not the one you use.

export const SETTINGS_GROUPS = [
  {
    id: 'practice',
    title: 'Practice',
    caption: "Your firm's own reference data and standard procedure.",
    items: [
      {
        slug: 'auditors',
        label: 'Auditors',
        description: 'In-house and external signatories, and their audit ceilings',
        keywords: 'auditor signatory statutory tax internal ceiling limit capacity',
        icon: AuditorIcon,
        Component: AuditorsManager,
      },
      {
        slug: 'checklists',
        label: 'Checklist Templates',
        description: 'The standard steps copied onto each generated task',
        keywords: 'checklist template steps compliance procedure',
        icon: ChecklistIcon,
        Component: ChecklistTemplateManager,
      },
      {
        slug: 'portals',
        label: 'Filing Portals',
        description: 'The sites your logins are stored against',
        keywords: 'portal website login credential gst incometax mca traces',
        icon: PortalIcon,
        Component: PortalsManager,
      },
    ],
  },
  {
    id: 'clients',
    title: 'Clients',
    caption: 'What your clients see and who handles them.',
    items: [
      {
        slug: 'chat',
        label: 'Client Chat',
        description: 'Turn chat on or off, and route each client to an owner',
        keywords: 'chat message conversation assignee routing portal staff names',
        icon: ChatIcon,
        Component: ChatSettingsManager,
      },
    ],
  },
  {
    id: 'people',
    title: 'People',
    caption: 'How your own staff are measured.',
    items: [
      {
        slug: 'attendance',
        label: 'Attendance',
        description: 'Standard hours, the work week and this year’s holidays',
        keywords: 'attendance clock hours work week holiday timezone overtime half day',
        icon: AttendanceIcon,
        Component: AttendanceSettingsManager,
      },
    ],
  },
  {
    id: 'billing',
    title: 'Billing',
    caption: 'How invoices are numbered, taxed and sent.',
    items: [
      {
        slug: 'invoicing',
        label: 'Invoicing & Tax',
        description: 'GST, TDS, number series and how documents reach clients',
        keywords: 'invoice gst tds proforma receipt number series prefix email smtp bank',
        icon: InvoiceIcon,
        // /api/finance sits behind requireNav('nav.finance'), so without this
        // the row would be visible to a settings-only admin and 403 on open.
        // Every other panel here needs nothing beyond a valid token.
        navKey: 'nav.finance',
        Component: FinanceSettingsCard,
      },
    ],
  },
  {
    id: 'security',
    title: 'Security',
    caption: 'Your own sign-in — not the organization’s.',
    items: [
      {
        slug: 'account-security',
        label: 'Sign-in & Sessions',
        description: 'Your password and the devices you are signed in on',
        keywords: 'password session device sign out security login two factor',
        icon: SecurityIcon,
        Component: SecurityPanel,
      },
    ],
  },
  {
    id: 'about',
    title: 'About',
    caption: 'Managed by your platform administrator.',
    items: [
      {
        slug: 'organization',
        label: 'Organization',
        description: 'Name, industry and description',
        keywords: 'organization name industry description slug company firm',
        icon: OrganizationIcon,
        Component: OrganizationPanel,
      },
      {
        slug: 'contact',
        label: 'Contact Information',
        description: 'The email and phone on your organization record',
        keywords: 'contact email phone address',
        icon: ContactIcon,
        Component: OrgContactPanel,
      },
      {
        slug: 'subscription',
        label: 'Subscription',
        description: 'Your plan, its status and seats in use',
        keywords: 'subscription plan seats users licence billing enterprise status',
        icon: SubscriptionIcon,
        Component: SubscriptionPanel,
      },
    ],
  },
];

// Flat lookup for the detail route. Built once from the groups above, so a
// screen can never exist in one and not the other.
export const SETTINGS_BY_SLUG = Object.fromEntries(
  SETTINGS_GROUPS.flatMap((g) => g.items.map((i) => [i.slug, { ...i, group: g }]))
);

/**
 * The groups this user may actually open, filtered by an optional search.
 *
 * The permission check and the search run in ONE place so the menu and the
 * detail route agree: a row hidden here is also blocked there (see
 * `canOpenSetting`), which is the same rule the cockpit sidebar follows.
 *
 * Search matches the label, the description and the hidden `keywords` —
 * somebody looking for "GST" should land on Invoicing without having to know
 * we called it that.
 */
export function filterSettings(query, user) {
  const q = (query || '').trim().toLowerCase();
  return SETTINGS_GROUPS
    .map((g) => ({
      ...g,
      items: g.items.filter((i) => {
        if (!canAccessNavKey(user, i.navKey || null)) return false;
        if (!q) return true;
        return `${i.label} ${i.description} ${i.keywords} ${g.title}`.toLowerCase().includes(q);
      }),
    }))
    .filter((g) => g.items.length > 0);
}

// Whether this user may open one screen. Used by the detail route, so typing a
// URL can't reach a section the menu hides.
export const canOpenSetting = (user, entry) =>
  !!entry && canAccessNavKey(user, entry.navKey || null);
